import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslate } from 'react-translate-plus';
import styled from 'styled-components';

import {
  Button,
  ContextMenu,
  ContextMenuLink,
  Icon,
  icons,
  PositioningPortalWithState,
  Tooltip,
} from '@hoffmann/pattern-library-2';

import { login } from '../../api/api-gateway/login';
import { logout } from '../../api/api-gateway/logout';
import { useAccess } from '../../hooks/useAccess';
import { useApp } from '../../hooks/useApp';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import Store from '../../interfaces/Store';
import { ID_TOKEN } from '../../providers/AppContext/AppContext';
import { isAnyAdminUser } from '../../services/UserRole';
import { useLocalisation } from '../../services/localisationContext';
import { getSmartProjectDashboardLink } from '../../services/locationService';
import { getWindow } from '../../services/windowService';
import { withStyledClasses } from '../../styled/withStyledClasses';
import { ConfigurationsMenu } from './ConfigurationsMenu';
import { Logo } from './Logo';
import { SideNavigation } from './SideNavigation';

const projectsPaths = ['/projects', '/sales-projects', '/drafts', '/test-projects'];
const configuratorPath = '/configurator/';

export const Navigation: FC = () => {
  const t = useTranslate('Notifications');
  const t2 = useTranslate('NavigationBar');
  const { loggedIn, roles, username, userId } = useApp();
  const { isPermittedToAccessAdminPage } = useAccess();
  const isAdminUser = isAnyAdminUser(roles);

  const { pathname = '' } = useLocation();
  const isProjectPages = projectsPaths.some((p) => pathname.startsWith(p));
  const isConfiguratorPage = pathname.startsWith(configuratorPath);
  const history = useNavigate();
  const configuration = useSelector((state: Store) => state.configuration);
  const [idToken] = useLocalStorage<string>(ID_TOKEN);
  const { currentLanguage } = useLocalisation();
  const [isSideNavigationOpen, setIsSideNavigationOpen] = useState(false);

  const href = t('USER_PROFILE_URL', {
    origin: t2('TITLE_CONFIGURATOR'),
    originUrl: getWindow().location.href,
  });

  const openUserProfile = () => {
    getWindow().open(href);
  };

  const redirectToProjects = async () => {
    const projectUrl = await getSmartProjectDashboardLink(
      configuration,
      userId,
      isPermittedToAccessAdminPage,
    );
    history(projectUrl);
  };

  return (
    <>
      <Header>
        <Menu>
          <Logo />
          <Buttons>
            {!isProjectPages && (
              <>
                {pathname !== '/' && (
                  <Button
                    label={t2('MENU_BUTTON_NEW_CONFIG')}
                    onClick={() => history('/')}
                    icon={<Icon source={icons.Add} />}
                    navigation
                  />
                )}

                <Button
                  label={t2('MENU_PROJECTS')}
                  navigation
                  onClick={() => (loggedIn ? redirectToProjects() : login(currentLanguage))}
                />
              </>
            )}
            {isProjectPages && (
              <PositioningPortalWithState
                portalContent={({ close }) => (
                  <ContextMenu onClose={close}>
                    <ContextMenuLink
                      label={t2('MENU_BUTTON_NEW_CONFIG')}
                      onClick={() => history('/')}
                      icon={<Icon source={icons.Add} />}
                    />
                  </ContextMenu>
                )}
              >
                {({ open }) => <Button label={t2('MENU_CONFIGURATOR')} navigation onClick={open} />}
              </PositioningPortalWithState>
            )}
            {(isConfiguratorPage && isAdminUser && (
              <ConfigurationsMenu configuration={configuration} />
            )) || <></>}
            <Divider />
            <CustomTooltip content={<>{t2('APPLICATIONS_TOOLTIP')}</>}>
              <ApplicationsButton
                onClick={() => setIsSideNavigationOpen(true)}
                icon={<Icon source={icons.Apps} />}
                navigation
                label={t2('APPLICATIONS')}
              />
            </CustomTooltip>
            <Divider />
            <LogInLogOut>
              {loggedIn && (
                <PositioningPortalWithState
                  portalContent={({ close }) => (
                    <ContextMenu onClose={close}>
                      <ContextMenuLink
                        label={t2('BUTTON_USER_PROFILE')}
                        href={href}
                        onClick={openUserProfile}
                        target="_blank"
                        icon={<Icon source={icons.Person} />}
                      />
                      <ContextMenuLink
                        label={t2('BUTTON_LOGOUT')}
                        onClick={() => logout(idToken, currentLanguage)}
                        icon={<Icon source={icons.Logout} />}
                      />
                    </ContextMenu>
                  )}
                  positionStrategy={(parentRect, portalRect) => ({
                    left: parentRect.left - portalRect.width + parentRect.width,
                    top: parentRect.bottom,
                  })}
                >
                  {({ open }) => (
                    <Username onClick={open} data-cy="username">
                      <span>{username}</span>
                      <Icon source={icons.ExpandMore} />
                    </Username>
                  )}
                </PositioningPortalWithState>
              )}
              {!loggedIn && (
                <Button
                  label={t2('BUTTON_LOGIN')}
                  navigation
                  onClick={() => login(currentLanguage)}
                />
              )}
            </LogInLogOut>
          </Buttons>
        </Menu>
      </Header>
      {isSideNavigationOpen && <SideNavigation onClose={() => setIsSideNavigationOpen(false)} />}
    </>
  );
};

const Header = styled.div`
  ${({ theme }) => `
    width: 100%;
    border-bottom: 1px ${theme.colors.layout30};
    background: ${theme.colors.white};
`}
`;

const Menu = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 1rem;
  font-size: 26px;
  font-weight: bold;
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: 0.5rem;
  }
`;

const Divider = styled.div`
  ${({ theme }) => `
  width: 1px;
  height: 3rem;
  background: ${theme.colors.layout40};
  margin: 0 2rem;
`}
`;

const LogInLogOut = styled.div`
  display: flex;
  padding-right: 16px;
`;

const Username = styled.button`
  ${({ theme }) => `

  font-size: 15px;
  font-weight: normal;
  color: rgba(38, 38, 38, 0.7);;
  padding: 8px;
  display: flex;
  align-items: center;

  * + * {
    margin-left: 0.5rem;
  }

  &:hover {
    background-color: ${theme.colors.layout40};
    color: ${theme.colors.layout100};
  }
`}
`;

const ApplicationsButton = styled(withStyledClasses(Button))`
  svg {
    font-size: 24px;
  }
`;

const CustomTooltip = styled(withStyledClasses(Tooltip))`
  height: 36px;
`;
